import i18n from '@/i18n'
import { parseISO } from 'date-fns'
import { supabase } from '@/lib/supabase'
import {
  accountDayKey,
  addAccountDays,
  differenceInAccountDays,
} from '@/lib/accountTimezone'
import { fetchAllRows, normalizeEmbed, useAsyncReport, useReportScope } from './useReportQuery'

export type DateRange = { from: Date; to: Date }

export type LeadEntryRow = {
  date: string
  leads: number
}

export type SalesByDayRow = {
  date: string
  won: number
  lost: number
  revenue: number
}

export type LossReasonRow = {
  reason: string
  count: number
}

export type SalesReportData = {
  rows: SalesByDayRow[]
  lossReasons: LossReasonRow[]
}

export type SellerSalesRow = {
  sellerId: string
  seller: string
  active: boolean
  leads: number
  opportunities: number
  won: number
  lost: number
  revenue: number
  averageTicket: number
  conversion: number
}

export type ResponseTimeRow = {
  sellerId: string
  seller: string
  responses: number
  averageSeconds: number
  medianSeconds: number
  minSeconds: number
  maxSeconds: number
  underFiveMinutesPercentage: number
}

export type CampaignSalesRow = {
  campaignId: string
  campaign: string
  leads: number
  sales: number
  revenue: number
  conversion: number
}

export type AiAppointmentRow = {
  eventId: string
  assistant: string
  title: string
  start: string
  end: string
  contactId: string | null
  contact: string
  phone: string
  htmlLink: string | null
}

type ContactRow = {
  id: string
  attribution_id: string | null
  created_at?: string | null
  phone?: string | null
  jid?: string | null
  name?: string | null
  display_name?: string | null
}

type OpportunityRow = {
  id: string
  contact_id: string | null
  status: 'open' | 'won' | 'lost'
  price: number | string | null
  created_at: string | null
  sale_date: string | null
  closed_at: string | null
  updated_at: string | null
  won_by_user_id: string | null
  loss_reason_id: string | null
  contact?: { attribution_id: string | null } | { attribution_id: string | null }[] | null
  loss_reason?: { name: string } | { name: string }[] | null
}

type AccountUserRow = {
  id: string
  auth_user_id: string | null
  name: string
  is_active: boolean
}

const EMPTY_SALES: SalesReportData = { rows: [], lossReasons: [] }

function assertClient(accountId: string | null) {
  if (!accountId) throw new Error(i18n.t('common:errors.noAccount'))
  if (!supabase) throw new Error(i18n.t('common:errors.notConfigured'))
  return supabase
}

/** Day buckets follow the account timezone, so a 22h lead lands on the same day for everyone. */
function dayKey(value: string) {
  return accountDayKey(value)
}

function dateBuckets(fromIso: string, toIso: string) {
  // `toIso` is an exclusive upper bound, so the last bucket is the day before it.
  const end = addAccountDays(toIso, -1)
  const days = differenceInAccountDays(end, fromIso)
  if (days < 0) return []

  return Array.from({ length: days + 1 }, (_, index) => ({
    date: accountDayKey(addAccountDays(fromIso, index)),
  }))
}

function closedAt(opportunity: OpportunityRow) {
  return opportunity.sale_date || opportunity.closed_at || opportunity.updated_at
}

function money(value: number | string | null) {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed) ? parsed : 0
}

async function loadUsers(accountId: string): Promise<AccountUserRow[]> {
  const client = assertClient(accountId)
  return fetchAllRows<AccountUserRow>((from, to) =>
    client
      .from('account_users')
      .select('id, auth_user_id, name, is_active')
      .eq('account_id', accountId)
      .order('name')
      .range(from, to),
  )
}

export function useLeadEntryReport(range: DateRange) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<LeadEntryRow[]>(
    async () => {
      const client = assertClient(scope.accountId)
      const contacts = await fetchAllRows<ContactRow>((from, to) =>
        scope.applyContactScope(
          client
            .from('account_contacts')
            .select('id, attribution_id, created_at')
            .eq('account_id', scope.accountId!)
            .eq('is_group', false)
            .gte('created_at', scope.fromIso)
            .lt('created_at', scope.toIso)
            .order('created_at')
            .range(from, to),
        ),
      )

      const counts = new Map<string, number>()
      contacts.forEach((contact) => {
        if (!contact.created_at) return
        const key = dayKey(contact.created_at)
        counts.set(key, (counts.get(key) || 0) + 1)
      })

      return dateBuckets(scope.fromIso, scope.toIso).map(({ date }) => ({
        date,
        leads: counts.get(date) || 0,
      }))
    },
    [],
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts],
  )
}

export function useSalesReport(range: DateRange) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<SalesReportData>(
    async () => {
      const client = assertClient(scope.accountId)
      const opportunities = await fetchAllRows<OpportunityRow>((from, to) =>
        client
          .from('account_opportunities')
          .select('id, contact_id, status, price, created_at, sale_date, closed_at, updated_at, won_by_user_id, loss_reason_id, contact:account_contacts(attribution_id), loss_reason:account_loss_reasons(name)')
          .eq('account_id', scope.accountId!)
          .in('status', ['won', 'lost'])
          .order('updated_at')
          .range(from, to),
      )

      const inScope = opportunities.filter((opportunity) => {
        const contact = normalizeEmbed(opportunity.contact)
        if (!scope.contactMatchesScope({ attribution_id: contact?.attribution_id ?? null })) return false
        const effectiveDate = closedAt(opportunity)
        return effectiveDate && effectiveDate >= scope.fromIso && effectiveDate < scope.toIso
      })

      const daily = new Map<string, Omit<SalesByDayRow, 'date'>>()
      const reasons = new Map<string, number>()
      inScope.forEach((opportunity) => {
        const effectiveDate = closedAt(opportunity)
        if (!effectiveDate) return
        const key = dayKey(effectiveDate)
        const current = daily.get(key) || { won: 0, lost: 0, revenue: 0 }
        if (opportunity.status === 'won') {
          current.won += 1
          current.revenue += money(opportunity.price)
        } else {
          current.lost += 1
          const reason = normalizeEmbed(opportunity.loss_reason)?.name || 'Sem motivo informado'
          reasons.set(reason, (reasons.get(reason) || 0) + 1)
        }
        daily.set(key, current)
      })

      return {
        rows: dateBuckets(scope.fromIso, scope.toIso).map(({ date }) => ({
          date,
          ...(daily.get(date) || { won: 0, lost: 0, revenue: 0 }),
        })),
        lossReasons: Array.from(reasons, ([reason, count]) => ({ reason, count }))
          .sort((a, b) => b.count - a.count),
      }
    },
    EMPTY_SALES,
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts],
  )
}

export function useSellerSalesReport(range: DateRange) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<SellerSalesRow[]>(
    async () => {
      const client = assertClient(scope.accountId)
      const [users, contacts, opportunities] = await Promise.all([
        loadUsers(scope.accountId!),
        fetchAllRows<ContactRow>((from, to) =>
          scope.applyContactScope(
            client
              .from('account_contacts')
              .select('id, attribution_id, created_at')
              .eq('account_id', scope.accountId!)
              .eq('is_group', false)
              .gte('created_at', scope.fromIso)
              .lt('created_at', scope.toIso)
              .range(from, to),
          ),
        ),
        fetchAllRows<OpportunityRow>((from, to) =>
          client
            .from('account_opportunities')
            .select('id, contact_id, status, price, created_at, sale_date, closed_at, updated_at, won_by_user_id, loss_reason_id, contact:account_contacts(attribution_id)')
            .eq('account_id', scope.accountId!)
            .range(from, to),
        ),
      ])

      const stats = new Map<string, Omit<SellerSalesRow, 'sellerId' | 'seller' | 'active' | 'averageTicket' | 'conversion'>>()
      users.forEach((user) => stats.set(user.id, { leads: 0, opportunities: 0, won: 0, lost: 0, revenue: 0 }))
      const userByAuth = new Map(users.filter((user) => user.auth_user_id).map((user) => [user.auth_user_id!, user]))

      contacts.forEach((contact) => {
        const seller = contact.attribution_id ? userByAuth.get(contact.attribution_id) : null
        if (seller) stats.get(seller.id)!.leads += 1
      })

      opportunities.forEach((opportunity) => {
        const contact = normalizeEmbed(opportunity.contact)
        if (!scope.contactMatchesScope({ attribution_id: contact?.attribution_id ?? null })) return
        const attributedSeller = contact?.attribution_id ? userByAuth.get(contact.attribution_id) : null

        if (opportunity.created_at && opportunity.created_at >= scope.fromIso && opportunity.created_at < scope.toIso && attributedSeller) {
          stats.get(attributedSeller.id)!.opportunities += 1
        }

        const effectiveDate = closedAt(opportunity)
        if (!effectiveDate || effectiveDate < scope.fromIso || effectiveDate >= scope.toIso) return

        if (opportunity.status === 'won' && opportunity.won_by_user_id && stats.has(opportunity.won_by_user_id)) {
          const row = stats.get(opportunity.won_by_user_id)!
          row.won += 1
          row.revenue += money(opportunity.price)
        } else if (opportunity.status === 'lost' && attributedSeller) {
          stats.get(attributedSeller.id)!.lost += 1
        }
      })

      return users.map((user) => {
        const row = stats.get(user.id)!
        const closed = row.won + row.lost
        return {
          sellerId: user.id,
          seller: user.name,
          active: user.is_active,
          ...row,
          averageTicket: row.won ? row.revenue / row.won : 0,
          conversion: closed ? (row.won / closed) * 100 : 0,
        }
      })
    },
    [],
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts],
  )
}

export function useResponseTimeReport(range: DateRange, maxHours = 24) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<ResponseTimeRow[]>(
    async () => {
      const client = assertClient(scope.accountId)
      type MessageRow = {
        id: string | number
        account_contact_id: string | null
        sent_at: string
        from_me: boolean
        is_from_ai: boolean | null
        is_followup: boolean | null
        contact?: { attribution_id: string | null } | { attribution_id: string | null }[] | null
      }

      const [users, messages] = await Promise.all([
        loadUsers(scope.accountId!),
        fetchAllRows<MessageRow>((from, to) =>
          client
            .from('account_messages')
            .select('id, account_contact_id, sent_at, from_me, is_from_ai, is_followup, contact:account_contacts(attribution_id)')
            .eq('account_id', scope.accountId!)
            .gte('sent_at', scope.fromIso)
            .lt('sent_at', scope.toIso)
            .order('sent_at')
            .range(from, to),
        ),
      ])

      const scopedMessages = messages.filter((message) => {
        const contact = normalizeEmbed(message.contact)
        return scope.contactMatchesScope({ attribution_id: contact?.attribution_id ?? null })
      })
      const byContact = new Map<string, MessageRow[]>()
      scopedMessages.forEach((message) => {
        if (!message.account_contact_id) return
        const list = byContact.get(message.account_contact_id) || []
        list.push(message)
        byContact.set(message.account_contact_id, list)
      })

      const userByAuth = new Map(users.filter((user) => user.auth_user_id).map((user) => [user.auth_user_id!, user]))
      const samples = new Map<string, number[]>()
      const maxSeconds = maxHours * 60 * 60

      byContact.forEach((contactMessages) => {
        let pendingAt: number | null = null
        contactMessages.sort((a, b) => a.sent_at.localeCompare(b.sent_at))
        contactMessages.forEach((message) => {
          if (!message.from_me) {
            if (pendingAt === null) pendingAt = parseISO(message.sent_at).getTime()
            return
          }
          if (message.is_from_ai || message.is_followup || pendingAt === null) return

          const delta = (parseISO(message.sent_at).getTime() - pendingAt) / 1000
          pendingAt = null
          if (delta < 0 || delta > maxSeconds) return

          const contact = normalizeEmbed(message.contact)
          const seller = contact?.attribution_id ? userByAuth.get(contact.attribution_id) : null
          if (!seller) return
          const current = samples.get(seller.id) || []
          current.push(delta)
          samples.set(seller.id, current)
        })
      })

      return users.map((user) => {
        const values = (samples.get(user.id) || []).sort((a, b) => a - b)
        const count = values.length
        const middle = Math.floor(count / 2)
        const median = count
          ? count % 2
            ? values[middle]
            : (values[middle - 1] + values[middle]) / 2
          : 0
        return {
          sellerId: user.id,
          seller: user.name,
          responses: count,
          averageSeconds: count ? values.reduce((sum, value) => sum + value, 0) / count : 0,
          medianSeconds: median,
          minSeconds: count ? values[0] : 0,
          maxSeconds: count ? values[count - 1] : 0,
          underFiveMinutesPercentage: count ? (values.filter((value) => value < 300).length / count) * 100 : 0,
        }
      })
    },
    [],
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts, maxHours],
  )
}

export function useCampaignSalesReport(range: DateRange) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<CampaignSalesRow[]>(
    async () => {
      const client = assertClient(scope.accountId)
      type CampaignTouch = {
        contact_id: string
        campaign_id: string
        campaign_name: string | null
        activated_at: string | null
      }

      const contacts = await fetchAllRows<ContactRow>((from, to) =>
        scope.applyContactScope(
          client
            .from('account_contacts')
            .select('id, attribution_id, created_at')
            .eq('account_id', scope.accountId!)
            .eq('is_group', false)
            .gte('created_at', scope.fromIso)
            .lt('created_at', scope.toIso)
            .range(from, to),
        ),
      )
      const cohort = new Set(contacts.map((contact) => contact.id))

      const [touches, wonOpportunities] = await Promise.all([
        fetchAllRows<CampaignTouch>((from, to) =>
          client
            .from('account_contact_campaigns')
            .select('contact_id, campaign_id, campaign_name, activated_at')
            .eq('account_id', scope.accountId!)
            .order('activated_at')
            .range(from, to),
        ),
        fetchAllRows<Pick<OpportunityRow, 'contact_id' | 'price'>>((from, to) =>
          client
            .from('account_opportunities')
            .select('contact_id, price')
            .eq('account_id', scope.accountId!)
            .eq('status', 'won')
            .range(from, to),
        ),
      ])

      const firstTouch = new Map<string, CampaignTouch>()
      touches.forEach((touch) => {
        if (!cohort.has(touch.contact_id)) return
        const current = firstTouch.get(touch.contact_id)
        if (!current || (touch.activated_at || '9999') < (current.activated_at || '9999')) {
          firstTouch.set(touch.contact_id, touch)
        }
      })

      const salesByContact = new Map<string, { sales: number; revenue: number }>()
      wonOpportunities.forEach((opportunity) => {
        if (!opportunity.contact_id || !cohort.has(opportunity.contact_id)) return
        const current = salesByContact.get(opportunity.contact_id) || { sales: 0, revenue: 0 }
        current.sales += 1
        current.revenue += money(opportunity.price)
        salesByContact.set(opportunity.contact_id, current)
      })

      const campaigns = new Map<string, Omit<CampaignSalesRow, 'campaignId' | 'conversion'>>()
      contacts.forEach((contact) => {
        const touch = firstTouch.get(contact.id)
        const id = touch?.campaign_id || '__organic__'
        const current = campaigns.get(id) || {
          campaign: touch?.campaign_name || i18n.t('common:shared.noCampaign'),
          leads: 0,
          sales: 0,
          revenue: 0,
        }
        const sales = salesByContact.get(contact.id)
        current.leads += 1
        current.sales += sales?.sales || 0
        current.revenue += sales?.revenue || 0
        campaigns.set(id, current)
      })

      return Array.from(campaigns, ([campaignId, row]) => ({
        campaignId,
        ...row,
        conversion: row.leads ? (row.sales / row.leads) * 100 : 0,
      })).sort((a, b) => b.revenue - a.revenue)
    },
    [],
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts],
  )
}

function phoneDigits(value: string | null | undefined) {
  return (value || '').replace(/\D/g, '')
}

function phonesMatch(left: string, right: string) {
  if (!left || !right) return false
  if (left === right) return true
  const length = Math.min(left.length, right.length, 11)
  return length >= 8 && left.slice(-length) === right.slice(-length)
}

function phonesFromDescription(description: string) {
  const marker = '— Dados do contato —'
  if (!description.includes(marker)) return []
  const details = description.slice(description.indexOf(marker) + marker.length)
  return Array.from(details.matchAll(/(?:\+?\d[\d\s().-]{7,}\d)/g), (match) => phoneDigits(match[0]))
    .filter((phone) => phone.length >= 8)
}

export type MarketingCreativeRow = {
  attributionKey: string
  title: string
  sourceApp: string
  leads: number
  responded: number
  opportunities: number
  sales: number
  revenue: number
  conversion: number
  averageTicket: number
}

export type MarketingFormBreakdownRow = {
  question: string
  answer: string
  leads: number
  sales: number
  revenue: number
  conversion: number
}

export type MarketingPlatformRow = {
  platform: string
  label: string
  leads: number
  responded: number
  opportunities: number
  sales: number
  revenue: number
  conversion: number
}

export type MarketingDailyRow = {
  date: string
  leads: number
  sales: number
}

export type MarketingPerformanceData = {
  totals: {
    leads: number
    responded: number
    opportunities: number
    sales: number
    revenue: number
    leadToSale: number
    averageFirstResponseSeconds: number
    facebook: number
    instagram: number
  }
  funnel: {
    leads: number
    responded: number
    opportunities: number
    sales: number
  }
  daily: MarketingDailyRow[]
  byPlatform: MarketingPlatformRow[]
  creatives: MarketingCreativeRow[]
  formBreakdown: MarketingFormBreakdownRow[]
}

const EMPTY_MARKETING: MarketingPerformanceData = {
  totals: {
    leads: 0,
    responded: 0,
    opportunities: 0,
    sales: 0,
    revenue: 0,
    leadToSale: 0,
    averageFirstResponseSeconds: 0,
    facebook: 0,
    instagram: 0,
  },
  funnel: { leads: 0, responded: 0, opportunities: 0, sales: 0 },
  daily: [],
  byPlatform: [],
  creatives: [],
  formBreakdown: [],
}

const NO_AD_KEY = '__no_ad__'

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  messenger: 'Messenger',
  meta: 'Meta',
  whatsapp: 'WhatsApp',
}

function platformLabel(platform: string): string {
  if (!platform) return 'Sem plataforma'
  return PLATFORM_LABELS[platform] ?? platform.charAt(0).toUpperCase() + platform.slice(1)
}

function chunk<T>(items: T[], size: number): T[][] {
  const groups: T[][] = []
  for (let i = 0; i < items.length; i += size) groups.push(items.slice(i, i + size))
  return groups
}

export function useMarketingPerformanceReport(range: DateRange) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<MarketingPerformanceData>(
    async () => {
      const client = assertClient(scope.accountId)

      type LeadMessageRow = {
        account_contact_id: string | null
        sent_at: string
        marketing_source_app: string | null
        marketing_attribution_key: string | null
        marketing_title: string | null
        marketing_form_responses: Record<string, string> | null
        contact?: { attribution_id: string | null } | { attribution_id: string | null }[] | null
      }

      const leadMessages = await fetchAllRows<LeadMessageRow>((from, to) =>
        client
          .from('account_messages')
          .select('account_contact_id, sent_at, marketing_source_app, marketing_attribution_key, marketing_title, marketing_form_responses, contact:account_contacts(attribution_id)')
          .eq('account_id', scope.accountId!)
          .eq('marketing_touch_type', 'inbound_lead')
          .gte('sent_at', scope.fromIso)
          .lt('sent_at', scope.toIso)
          .order('sent_at')
          .range(from, to),
      )

      type LeadInfo = {
        contactId: string
        sentAt: string
        sourceApp: string
        attributionKey: string
        title: string
        form: Record<string, string> | null
      }

      const leadByContact = new Map<string, LeadInfo>()
      leadMessages.forEach((message) => {
        if (!message.account_contact_id) return
        const contact = normalizeEmbed(message.contact)
        if (!scope.contactMatchesScope({ attribution_id: contact?.attribution_id ?? null })) return

        const existing = leadByContact.get(message.account_contact_id)
        if (existing && existing.sentAt <= message.sent_at) return

        leadByContact.set(message.account_contact_id, {
          contactId: message.account_contact_id,
          sentAt: message.sent_at,
          sourceApp: (message.marketing_source_app || '').toLowerCase(),
          attributionKey: message.marketing_attribution_key || NO_AD_KEY,
          title: message.marketing_title || 'Sem ID de anúncio',
          form: message.marketing_form_responses || null,
        })
      })

      const leads = Array.from(leadByContact.values())
      const cohortIds = leads.map((lead) => lead.contactId)

      if (cohortIds.length === 0) return EMPTY_MARKETING

      type ThreadMessageRow = {
        account_contact_id: string | null
        sent_at: string
        from_me: boolean
        is_from_ai: boolean | null
        is_followup: boolean | null
        marketing_touch_type: string | null
      }
      type CohortOpportunityRow = {
        contact_id: string | null
        status: 'open' | 'won' | 'lost'
        price: number | string | null
        sale_date: string | null
        closed_at: string | null
        updated_at: string | null
      }

      const idChunks = chunk(cohortIds, 200)

      const threadMessages: ThreadMessageRow[] = []
      const opportunities: CohortOpportunityRow[] = []

      for (const ids of idChunks) {
        const [threads, opps] = await Promise.all([
          fetchAllRows<ThreadMessageRow>((from, to) =>
            client
              .from('account_messages')
              .select('account_contact_id, sent_at, from_me, is_from_ai, is_followup, marketing_touch_type')
              .eq('account_id', scope.accountId!)
              .in('account_contact_id', ids)
              .eq('from_me', true)
              .order('sent_at')
              .range(from, to),
          ),
          fetchAllRows<CohortOpportunityRow>((from, to) =>
            client
              .from('account_opportunities')
              .select('contact_id, status, price, sale_date, closed_at, updated_at')
              .eq('account_id', scope.accountId!)
              .in('contact_id', ids)
              .range(from, to),
          ),
        ])
        threadMessages.push(...threads)
        opportunities.push(...opps)
      }

      const firstHumanReply = new Map<string, string>()
      threadMessages.forEach((message) => {
        if (!message.account_contact_id) return
        if (message.is_from_ai || message.is_followup) return
        // Saudação automática do anúncio não conta como atendimento humano.
        if (message.marketing_touch_type === 'automated_greeting') return
        const lead = leadByContact.get(message.account_contact_id)
        if (!lead || message.sent_at <= lead.sentAt) return
        const current = firstHumanReply.get(message.account_contact_id)
        if (!current || message.sent_at < current) {
          firstHumanReply.set(message.account_contact_id, message.sent_at)
        }
      })

      const contactHasOpp = new Set<string>()
      const contactWon = new Set<string>()
      const contactRevenue = new Map<string, number>()
      const wonEffectiveDate = new Map<string, string>()
      opportunities.forEach((opportunity) => {
        if (!opportunity.contact_id || !leadByContact.has(opportunity.contact_id)) return
        contactHasOpp.add(opportunity.contact_id)
        if (opportunity.status === 'won') {
          contactWon.add(opportunity.contact_id)
          contactRevenue.set(
            opportunity.contact_id,
            (contactRevenue.get(opportunity.contact_id) || 0) + money(opportunity.price),
          )
          const effective = closedAt(opportunity as unknown as OpportunityRow)
          if (effective) {
            const current = wonEffectiveDate.get(opportunity.contact_id)
            if (!current || effective < current) wonEffectiveDate.set(opportunity.contact_id, effective)
          }
        }
      })

      const responseSamples: number[] = []
      leads.forEach((lead) => {
        const reply = firstHumanReply.get(lead.contactId)
        if (!reply) return
        const delta = (parseISO(reply).getTime() - parseISO(lead.sentAt).getTime()) / 1000
        if (delta >= 0) responseSamples.push(delta)
      })

      const totalLeads = leads.length
      const responded = firstHumanReply.size
      const opportunitiesCount = contactHasOpp.size
      const sales = contactWon.size
      const revenue = Array.from(contactRevenue.values()).reduce((sum, value) => sum + value, 0)
      const facebook = leads.filter((lead) => lead.sourceApp === 'facebook').length
      const instagram = leads.filter((lead) => lead.sourceApp === 'instagram').length
      const averageFirstResponseSeconds = responseSamples.length
        ? responseSamples.reduce((sum, value) => sum + value, 0) / responseSamples.length
        : 0

      const dailyLeads = new Map<string, number>()
      leads.forEach((lead) => {
        const key = dayKey(lead.sentAt)
        dailyLeads.set(key, (dailyLeads.get(key) || 0) + 1)
      })
      const dailySales = new Map<string, number>()
      wonEffectiveDate.forEach((effective) => {
        if (effective < scope.fromIso || effective >= scope.toIso) return
        const key = dayKey(effective)
        dailySales.set(key, (dailySales.get(key) || 0) + 1)
      })
      const daily = dateBuckets(scope.fromIso, scope.toIso).map(({ date }) => ({
        date,
        leads: dailyLeads.get(date) || 0,
        sales: dailySales.get(date) || 0,
      }))

      const creativeMap = new Map<string, Omit<MarketingCreativeRow, 'conversion' | 'averageTicket'>>()
      leads.forEach((lead) => {
        const current = creativeMap.get(lead.attributionKey) || {
          attributionKey: lead.attributionKey,
          title: lead.title,
          sourceApp: lead.sourceApp || '—',
          leads: 0,
          responded: 0,
          opportunities: 0,
          sales: 0,
          revenue: 0,
        }
        current.leads += 1
        if (firstHumanReply.has(lead.contactId)) current.responded += 1
        if (contactHasOpp.has(lead.contactId)) current.opportunities += 1
        if (contactWon.has(lead.contactId)) {
          current.sales += 1
          current.revenue += contactRevenue.get(lead.contactId) || 0
        }
        creativeMap.set(lead.attributionKey, current)
      })
      const creatives = Array.from(creativeMap.values())
        .map((row) => ({
          ...row,
          conversion: row.leads ? (row.sales / row.leads) * 100 : 0,
          averageTicket: row.sales ? row.revenue / row.sales : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue || b.leads - a.leads)

      const platformMap = new Map<string, Omit<MarketingPlatformRow, 'conversion' | 'label'>>()
      leads.forEach((lead) => {
        const platform = lead.sourceApp || ''
        const current = platformMap.get(platform) || {
          platform,
          leads: 0,
          responded: 0,
          opportunities: 0,
          sales: 0,
          revenue: 0,
        }
        current.leads += 1
        if (firstHumanReply.has(lead.contactId)) current.responded += 1
        if (contactHasOpp.has(lead.contactId)) current.opportunities += 1
        if (contactWon.has(lead.contactId)) {
          current.sales += 1
          current.revenue += contactRevenue.get(lead.contactId) || 0
        }
        platformMap.set(platform, current)
      })
      const byPlatform = Array.from(platformMap.values())
        .map((row) => ({
          ...row,
          label: platformLabel(row.platform),
          conversion: row.leads ? (row.sales / row.leads) * 100 : 0,
        }))
        .sort((a, b) => b.leads - a.leads)

      const formMap = new Map<string, Omit<MarketingFormBreakdownRow, 'conversion'>>()
      leads.forEach((lead) => {
        if (!lead.form) return
        const won = contactWon.has(lead.contactId)
        const leadRevenue = contactRevenue.get(lead.contactId) || 0
        Object.entries(lead.form).forEach(([question, answer]) => {
          if (!answer) return
          const key = `${question}||${answer}`
          const current = formMap.get(key) || { question, answer, leads: 0, sales: 0, revenue: 0 }
          current.leads += 1
          if (won) {
            current.sales += 1
            current.revenue += leadRevenue
          }
          formMap.set(key, current)
        })
      })
      const formBreakdown = Array.from(formMap.values())
        .map((row) => ({ ...row, conversion: row.leads ? (row.sales / row.leads) * 100 : 0 }))
        .sort((a, b) => a.question.localeCompare(b.question) || b.leads - a.leads)

      return {
        totals: {
          leads: totalLeads,
          responded,
          opportunities: opportunitiesCount,
          sales,
          revenue,
          leadToSale: totalLeads ? (sales / totalLeads) * 100 : 0,
          averageFirstResponseSeconds,
          facebook,
          instagram,
        },
        funnel: {
          leads: totalLeads,
          responded,
          opportunities: opportunitiesCount,
          sales,
        },
        daily,
        byPlatform,
        creatives,
        formBreakdown,
      }
    },
    EMPTY_MARKETING,
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts],
  )
}

export function useAiAppointmentsReport(range: DateRange) {
  const scope = useReportScope(range.from, range.to)

  return useAsyncReport<AiAppointmentRow[]>(
    async () => {
      const client = assertClient(scope.accountId)
      type AssistantIntegration = {
        assistant_id: string
        assistant?: { name: string } | { name: string }[] | null
      }
      type CalendarEvent = {
        id: string
        title?: string
        description?: string
        start: string
        end: string
        html_link?: string
      }

      const [contacts, integrations, sessionResult] = await Promise.all([
        fetchAllRows<ContactRow>((from, to) =>
          scope.applyContactScope(
            client
              .from('account_contacts')
              .select('id, attribution_id, phone, jid, name, display_name')
              .eq('account_id', scope.accountId!)
              .eq('is_group', false)
              .range(from, to),
          ),
        ),
        fetchAllRows<AssistantIntegration>((from, to) =>
          client
            .from('account_assistant_google_integrations')
            .select('assistant_id, assistant:account_assistants(name)')
            .eq('account_id', scope.accountId!)
            .eq('is_active', true)
            .range(from, to),
        ),
        client.auth.getSession(),
      ])

      const token = sessionResult.data.session?.access_token
      if (!token) throw new Error(i18n.t('common:errors.sessionExpired'))
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

      const responses = await Promise.all(
        integrations.map(async (integration) => {
          const response = await fetch(`${supabaseUrl}/functions/v1/google-calendar-api`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              account_id: scope.accountId,
              assistant_id: integration.assistant_id,
              action: 'list_events',
              params: {
                time_min: scope.fromIso,
                time_max: scope.toIso,
                max_results: 2500,
              },
            }),
          })
          const result = await response.json()
          if (!response.ok) {
            throw new Error(result.error || 'Erro ao consultar Google Calendar')
          }
          return {
            assistant: normalizeEmbed(integration.assistant)?.name || 'Assistente',
            events: (result.events || []) as CalendarEvent[],
          }
        }),
      )

      const contactPhones = contacts.flatMap((contact) => {
        const values = [phoneDigits(contact.phone), phoneDigits(contact.jid)]
        return values.filter(Boolean).map((phone) => ({ contact, phone }))
      })

      return responses
        .flatMap(({ assistant, events }) =>
          events.flatMap((event) => {
            const phones = phonesFromDescription(event.description || '')
            if (!phones.length) return []
            const matched = contactPhones.find(({ phone }) =>
              phones.some((eventPhone) => phonesMatch(phone, eventPhone)),
            )
            if (scope.isContactScopeRestricted && !matched) return []
            return [{
              eventId: event.id,
              assistant,
              title: event.title || 'Agendamento',
              start: event.start,
              end: event.end,
              contactId: matched?.contact.id || null,
              contact: matched
                ? matched.contact.name || matched.contact.display_name || matched.phone
                : 'Contato não localizado',
              phone: phones[0],
              htmlLink: event.html_link || null,
            }]
          }),
        )
        .sort((a, b) => a.start.localeCompare(b.start))
    },
    [],
    [scope.accountId, scope.fromIso, scope.toIso, scope.user?.auth_user_id, scope.user?.view_all_contacts, scope.user?.view_unassigned_contacts],
  )
}
